<template>
  <div class="ai-writing-test p-test-page">
    <!-- Pineapple Header -->
    <a-page-head title="AI 작문 테스트" :breadcrumbs="[{ label: '테스트' }, { label: 'AI 작문' }]">
      <template #actions>
        <a-btn v-if="hasData" kind="ghost" size="md" @click="resetForm">
          <template #icon>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="1 4 1 10 7 10"/>
              <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
            </svg>
          </template>
          초기화
        </a-btn>
        <a-btn v-if="hasData" kind="primary" size="md" :disabled="isSaving" @click="saveData">
          <template #icon>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
              <polyline points="17 21 17 13 7 13 7 21"/>
              <polyline points="7 3 7 8 15 8"/>
            </svg>
          </template>
          {{ isSaving ? '저장 중...' : '저장' }}
        </a-btn>
      </template>
    </a-page-head>

    <!-- Configuration Card -->
    <div class="card mb-4">
      <div class="card-header bg-gradient-primary text-white">
        <h5 class="mb-0 text-white">
          <i class="material-icons me-2" style="vertical-align: middle; font-size: 20px;">settings</i>
          교재 정보 설정
        </h5>
      </div>
      <div class="card-body">
        <div class="row">
          <div class="col-md-4 mb-3">
            <label class="form-label fw-bold">
              교재 바코드 <span class="text-danger">*</span>
            </label>
            <input
              v-model="config.barcode"
              type="text"
              class="form-control"
              placeholder="예: 9781635190151"
              required
            />
            <small class="text-muted">교재의 ISBN 또는 바코드 번호</small>
          </div>
          <div class="col-md-4 mb-3">
            <label class="form-label fw-bold">
              챕터 번호 <span class="text-danger">*</span>
            </label>
            <input
              v-model.number="config.chapterNumber"
              type="number"
              class="form-control"
              placeholder="예: 1"
              min="1"
              required
            />
            <small class="text-muted">작성할 내용의 챕터 번호</small>
          </div>
          <div class="col-md-4 mb-3">
            <label class="form-label fw-bold">
              고유키 <span class="text-danger">*</span>
            </label>
            <input
              v-model="config.uniqueKey"
              type="text"
              class="form-control"
              placeholder="예: 1757059361768"
              required
            />
            <small class="text-muted">콘텐츠 식별용 고유 키</small>
          </div>
          <div class="col-md-6 mb-3">
            <label class="form-label fw-bold">타겟 언어</label>
            <select v-model="config.targetLanguage" class="form-select">
              <option value="ko">한국어 (Korean)</option>
              <option value="en">영어 (English)</option>
              <option value="ja">일본어 (Japanese)</option>
              <option value="zh">중국어 (Chinese)</option>
              <option value="es">스페인어 (Spanish)</option>
            </select>
            <small class="text-muted">학습 대상 언어</small>
          </div>
          <div class="col-md-6 mb-3">
            <label class="form-label fw-bold">모국어</label>
            <select v-model="config.nativeLanguage" class="form-select">
              <option value="en">영어 (English)</option>
              <option value="ko">한국어 (Korean)</option>
              <option value="ja">일본어 (Japanese)</option>
              <option value="zh">중국어 (Chinese)</option>
              <option value="es">스페인어 (Spanish)</option>
            </select>
            <small class="text-muted">학습자의 모국어</small>
          </div>
        </div>
      </div>
    </div>

    <!-- Passage Writing Section -->
    <div class="card mb-4">
      <div class="card-header bg-gradient-success text-white">
        <h5 class="mb-0 text-white">
          <i class="material-icons me-2" style="vertical-align: middle; font-size: 20px;">article</i>
          Passage 작성 (Passage Script)
        </h5>
      </div>
      <div class="card-body">
        <div class="row">
          <div class="col-md-6 mb-4">
            <div class="mb-3">
              <label class="form-label fw-bold">
                타겟 언어 Passage ({{ getLanguageName(config.targetLanguage) }})
                <span class="text-danger">*</span>
              </label>
              <textarea
                v-model="essayScript.targetEssay"
                class="form-control"
                rows="10"
                :placeholder="`${getLanguageName(config.targetLanguage)}로 Passage를 작성하세요...`"
                required
              ></textarea>
              <div class="mt-2 text-end">
                <small class="text-muted">
                  글자 수: {{ (essayScript.targetEssay || '').length }}자
                </small>
              </div>
            </div>
          </div>
          <div class="col-md-6 mb-4">
            <div class="mb-3">
              <label class="form-label fw-bold">
                모국어 Passage ({{ getLanguageName(config.nativeLanguage) }})
                <span class="text-danger">*</span>
              </label>
              <textarea
                v-model="essayScript.nativeEssay"
                class="form-control"
                rows="10"
                :placeholder="`${getLanguageName(config.nativeLanguage)}로 Passage를 번역/작성하세요...`"
                required
              ></textarea>
              <div class="mt-2 text-end">
                <small class="text-muted">
                  글자 수: {{ (essayScript.nativeEssay || '').length }}자
                </small>
              </div>
            </div>
          </div>
        </div>
        <div class="alert alert-info mb-0">
          <i class="material-icons me-2" style="vertical-align: middle;">info</i>
          <strong>작성 가이드:</strong> 교재 내용을 바탕으로 타겟 언어로 Passage를 작성하고,
          학습자가 이해할 수 있도록 모국어로 번역 또는 설명을 추가해주세요.
        </div>
      </div>
    </div>

    <!-- Q&A Section -->
    <div class="card mb-4">
      <div class="card-header bg-gradient-info text-white">
        <div class="d-flex justify-content-between align-items-center">
          <h5 class="mb-0 text-white">
            <i class="material-icons me-2" style="vertical-align: middle; font-size: 20px;">quiz</i>
            질문과 답변 (Question & Answer)
          </h5>
          <button
            @click="addQAPair"
            class="btn btn-sm btn-white"
            title="Q&A 추가"
          >
            <i class="material-icons" style="font-size: 16px;">add</i>
            Q&A 추가
          </button>
        </div>
      </div>
      <div class="card-body">
        <div class="alert alert-warning mb-4">
          <i class="material-icons me-2" style="vertical-align: middle;">help_outline</i>
          <strong>작성 안내:</strong> Passage 내용을 기반으로 타겟 언어({{ getLanguageName(config.targetLanguage) }})로
          질문과 답변을 작성하세요. 빈 값도 허용됩니다.
        </div>

        <div v-if="questionAndAnswer.questions.length === 0" class="text-center py-4">
          <i class="material-icons" style="font-size: 48px; color: #9e9e9e;">quiz</i>
          <p class="text-muted mt-2">Q&A 추가 버튼을 클릭하여 질문과 답변을 작성하세요</p>
        </div>

        <div v-else class="qa-container">
          <div
            v-for="(_, index) in questionAndAnswer.questions"
            :key="index"
            class="qa-pair mb-4"
          >
            <div class="qa-header d-flex justify-content-between align-items-center mb-2">
              <span class="badge bg-primary">
                Q&A #{{ index + 1 }}
              </span>
              <button
                @click="removeQAPair(index)"
                class="btn btn-sm btn-outline-danger"
                title="삭제"
              >
                <i class="material-icons" style="font-size: 16px;">delete</i>
              </button>
            </div>
            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label">
                  <i class="material-icons me-1" style="font-size: 16px; vertical-align: middle;">help</i>
                  질문 ({{ getLanguageName(config.targetLanguage) }})
                </label>
                <textarea
                  v-model="questionAndAnswer.questions[index]"
                  class="form-control"
                  rows="3"
                  :placeholder="`질문 ${index + 1}을 입력하세요 (선택사항)`"
                ></textarea>
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">
                  <i class="material-icons me-1" style="font-size: 16px; vertical-align: middle;">check_circle</i>
                  답변 ({{ getLanguageName(config.targetLanguage) }})
                </label>
                <textarea
                  v-model="questionAndAnswer.answers[index]"
                  class="form-control"
                  rows="3"
                  :placeholder="`답변 ${index + 1}을 입력하세요 (선택사항)`"
                ></textarea>
              </div>
            </div>
          </div>
        </div>

        <div class="text-center mt-4">
          <button
            @click="addMultipleQAPairs"
            class="btn btn-outline-primary"
          >
            <i class="material-icons me-2" style="vertical-align: middle;">add_circle</i>
            여러 Q&A 한번에 추가
          </button>
        </div>
      </div>
    </div>

    <!-- Stage 1 Evaluation Section -->
    <div class="card mb-4" v-if="hasData && !stage1Result">
      <div class="card-header bg-gradient-danger text-white">
        <h5 class="mb-0 text-white">
          <i class="material-icons me-2" style="vertical-align: middle; font-size: 20px;">rate_review</i>
          Stage 1 평가 (초기 작성 평가)
        </h5>
      </div>
      <div class="card-body">
        <div class="alert alert-info mb-4">
          <i class="material-icons me-2" style="vertical-align: middle;">info</i>
          <strong>Stage 1 평가:</strong> 작성한 Passage와 Q&A를 AI가 평가하고 수정된 버전을 제공합니다.
        </div>

        <!-- 평가 진행 중 상태 표시 -->
        <div v-if="isEvaluatingStage1" class="evaluation-progress mb-4">
          <div class="progress-header mb-3">
            <h6 class="fw-bold text-center">
              <i class="material-icons rotating" style="vertical-align: middle;">sync</i>
              AI가 Passage를 분석하고 있습니다...
            </h6>
            <p class="text-center text-muted mb-0">
              <small>⏱️ 평가에는 약 30초~1분 정도 소요됩니다</small>
            </p>
          </div>
          <div class="progress" style="height: 25px;">
            <div
              class="progress-bar progress-bar-striped progress-bar-animated bg-danger"
              role="progressbar"
              style="width: 100%"
            >
              평가 진행 중...
            </div>
          </div>
          <div class="mt-3 text-center">
            <div class="spinner-border text-danger" role="status">
              <span class="visually-hidden">로딩 중...</span>
            </div>
          </div>
        </div>

        <div class="d-flex justify-content-center" v-if="!isEvaluatingStage1">
          <button
            @click="evaluateStage1"
            class="btn btn-lg btn-danger text-white"
            :disabled="isEvaluatingStage1"
          >
            <i class="material-icons me-2" style="vertical-align: middle;">play_arrow</i>
            Stage 1 평가 시작
          </button>
        </div>
      </div>
    </div>

    <!-- Stage 1 Result Card -->
    <div class="card mb-4" v-if="stage1Result">
      <div class="card-header bg-gradient-success text-white">
        <h5 class="mb-0 text-white">
          <i class="material-icons me-2" style="vertical-align: middle; font-size: 20px;">check_circle</i>
          Stage 1 평가 결과
        </h5>
      </div>
      <div class="card-body">
        <div class="alert alert-success mb-4">
          <i class="material-icons me-2" style="vertical-align: middle;">check</i>
          <strong>평가 완료!</strong> AI가 작성한 수정 버전을 확인하고, Stage 3으로 진행하세요.
        </div>

        <h6 class="fw-bold mb-3">AI 수정 Passage</h6>
        <div class="revised-content mb-4">
          <pre class="revised-text">{{ getAIEssay() }}</pre>
        </div>

        <h6 class="fw-bold mb-3">AI 수정 질문 & 답변</h6>
        <div class="table-responsive mb-4" v-if="getAIQuestions().length > 0">
          <table class="table table-bordered">
            <thead class="table-light">
              <tr>
                <th width="10%">#</th>
                <th width="45%">질문</th>
                <th width="45%">답변</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(q, idx) in getAIQuestions()" :key="idx">
                <td class="text-center">{{ idx + 1 }}</td>
                <td>{{ q }}</td>
                <td>{{ getAIAnswers()[idx] || '-' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="alert alert-info">
          <i class="material-icons me-2" style="vertical-align: middle;">info</i>
          AI가 수정한 질문과 답변이 없습니다.
        </div>

        <div v-if="stage1Result.overallReview" class="overall-review mb-4">
          <h6 class="fw-bold mb-3">종합 평가</h6>
          <div class="card bg-light">
            <div class="card-body">
              <p class="mb-2"><strong>요약:</strong> {{ stage1Result.overallReview.summary }}</p>
              <p class="mb-2" v-if="stage1Result.overallReview.strengths && stage1Result.overallReview.strengths.length > 0">
                <strong>강점:</strong>
                <ul class="mb-0 mt-1">
                  <li v-for="(strength, idx) in stage1Result.overallReview.strengths" :key="idx">{{ strength }}</li>
                </ul>
              </p>
              <p class="mb-0" v-if="stage1Result.overallReview.improvements && stage1Result.overallReview.improvements.length > 0">
                <strong>개선점:</strong>
                <ul class="mb-0 mt-1">
                  <li v-for="(improvement, idx) in stage1Result.overallReview.improvements" :key="idx">{{ improvement }}</li>
                </ul>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Stage 3 Input Section -->
    <div class="card mb-4" v-if="stage1Result && !stage3Result">
      <div class="card-header bg-gradient-warning">
        <h5 class="mb-0 text-dark">
          <i class="material-icons me-2" style="vertical-align: middle; font-size: 20px;">edit</i>
          Stage 3 스크립트 작성
        </h5>
      </div>
      <div class="card-body">
        <div class="alert alert-warning mb-4">
          <i class="material-icons me-2" style="vertical-align: middle;">info</i>
          <strong>Stage 3 평가:</strong> AI 수정 내용을 확인하고, 두 개의 스크립트를 작성하여 최종 평가를 받으세요.
        </div>

        <div class="mb-4">
          <label class="form-label fw-bold">
            First Script <span class="text-danger">*</span>
          </label>
          <textarea
            v-model="stage3Script.firstScript"
            class="form-control"
            rows="8"
            placeholder="AI 수정 내용을 보고 느낀 점이나 개선 사항에 대해 작성하세요..."
            required
          ></textarea>
          <small class="text-muted">
            AI가 수정한 Passage와 Q&A를 비교하여, 어떤 부분이 수정되었는지, 왜 수정되었는지에 대해 작성하세요.
          </small>
        </div>

        <div class="mb-4">
          <label class="form-label fw-bold">
            Second Script <span class="text-danger">*</span>
          </label>
          <textarea
            v-model="stage3Script.secondScript"
            class="form-control"
            rows="8"
            placeholder="AI의 수정 내용에 대한 전반적인 평가와 학습한 내용을 작성하세요..."
            required
          ></textarea>
          <small class="text-muted">
            AI의 피드백이 얼마나 도움이 되었는지, 앞으로 어떻게 개선할 것인지에 대해 작성하세요.
          </small>
        </div>

        <!-- 평가 진행 중 상태 표시 -->
        <div v-if="isEvaluatingStage3" class="evaluation-progress mb-4">
          <div class="progress-header mb-3">
            <h6 class="fw-bold text-center">
              <i class="material-icons rotating" style="vertical-align: middle;">sync</i>
              AI가 스크립트를 분석하고 있습니다...
            </h6>
            <p class="text-center text-muted mb-0">
              <small>⏱️ 평가에는 약 30초~1분 정도 소요됩니다</small>
            </p>
          </div>
          <div class="progress" style="height: 25px;">
            <div
              class="progress-bar progress-bar-striped progress-bar-animated bg-warning"
              role="progressbar"
              style="width: 100%"
            >
              평가 진행 중...
            </div>
          </div>
          <div class="mt-3 text-center">
            <div class="spinner-border text-warning" role="status">
              <span class="visually-hidden">로딩 중...</span>
            </div>
          </div>
        </div>

        <div class="d-flex justify-content-center" v-if="!isEvaluatingStage3">
          <button
            @click="evaluateStage3"
            class="btn btn-lg btn-warning"
            :disabled="isEvaluatingStage3 || !stage3Script.firstScript.trim() || !stage3Script.secondScript.trim()"
          >
            <i class="material-icons me-2" style="vertical-align: middle;">send</i>
            Stage 3 평가 시작
          </button>
        </div>
      </div>
    </div>

    <!-- Stage 3 Result Card -->
    <div class="card mb-4" v-if="stage3Result">
      <div class="card-header bg-gradient-primary text-white">
        <h5 class="mb-0 text-white">
          <i class="material-icons me-2" style="vertical-align: middle; font-size: 20px;">stars</i>
          Stage 3 최종 평가 결과
        </h5>
      </div>
      <div class="card-body">
        <div class="alert alert-primary mb-4">
          <i class="material-icons me-2" style="vertical-align: middle;">celebration</i>
          <strong>최종 평가 완료!</strong> 모든 단계의 평가가 완료되었습니다.
        </div>

        <div v-if="stage3Result.evalResults" class="eval-results mb-4">
          <h6 class="fw-bold mb-3">평가 결과</h6>

          <!-- First Script 평가 결과 -->
          <div v-if="stage3Result.evalResults.firstScriptResults" class="card bg-light mb-3">
            <div class="card-header">
              <strong>First Script 평가</strong>
            </div>
            <div class="card-body">
              <div class="mb-3">
                <div class="d-flex align-items-center mb-2">
                  <span class="fw-bold me-2">명확성 (Clear):</span>
                  <span class="badge" :class="stage3Result.evalResults.firstScriptResults.result?.clear ? 'bg-success' : 'bg-danger'">
                    {{ stage3Result.evalResults.firstScriptResults.result?.clear ? '✓ Yes' : '✗ No' }}
                  </span>
                </div>
                <div class="d-flex align-items-center mb-2">
                  <span class="fw-bold me-2">완성도 (Completed):</span>
                  <span class="badge" :class="stage3Result.evalResults.firstScriptResults.result?.completed ? 'bg-success' : 'bg-danger'">
                    {{ stage3Result.evalResults.firstScriptResults.result?.completed ? '✓ Yes' : '✗ No' }}
                  </span>
                </div>
              </div>
              <div v-if="stage3Result.evalResults.firstScriptResults.feedback" class="feedback-section">
                <h6 class="fw-bold mb-2">피드백:</h6>
                <div class="alert alert-info mb-0">
                  <p class="mb-0">{{ stage3Result.evalResults.firstScriptResults.feedback.koFeedback }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Second Script 평가 결과 (오타: secondScruptResults) -->
          <div v-if="stage3Result.evalResults.secondScruptResults" class="card bg-light mb-3">
            <div class="card-header">
              <strong>Second Script 평가</strong>
            </div>
            <div class="card-body">
              <div class="mb-3">
                <div class="d-flex align-items-center mb-2">
                  <span class="fw-bold me-2">명확성 (Clear):</span>
                  <span class="badge" :class="stage3Result.evalResults.secondScruptResults.result?.clear ? 'bg-success' : 'bg-danger'">
                    {{ stage3Result.evalResults.secondScruptResults.result?.clear ? '✓ Yes' : '✗ No' }}
                  </span>
                </div>
                <div class="d-flex align-items-center mb-2">
                  <span class="fw-bold me-2">완성도 (Completed):</span>
                  <span class="badge" :class="stage3Result.evalResults.secondScruptResults.result?.completed ? 'bg-success' : 'bg-danger'">
                    {{ stage3Result.evalResults.secondScruptResults.result?.completed ? '✓ Yes' : '✗ No' }}
                  </span>
                </div>
              </div>
              <div v-if="stage3Result.evalResults.secondScruptResults.feedback" class="feedback-section">
                <h6 class="fw-bold mb-2">피드백:</h6>
                <div class="alert alert-info mb-0">
                  <p class="mb-0">{{ stage3Result.evalResults.secondScruptResults.feedback.koFeedback }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 종합 평가 -->
        <div v-if="stage3Result.evalResults && stage3Result.evalResults.overallReview" class="overall-review">
          <h6 class="fw-bold mb-3">종합 평가</h6>
          <div class="card bg-light">
            <div class="card-body">
              <div class="mb-3">
                <strong>한국어 평가:</strong>
                <p class="mt-2 mb-0" style="white-space: pre-wrap;">{{ stage3Result.evalResults.overallReview.koOverallReview }}</p>
              </div>
            </div>
          </div>
        </div>

        <div class="mt-4 d-flex justify-content-center">
          <button @click="resetEvaluation" class="btn btn-secondary">
            <i class="material-icons me-2" style="vertical-align: middle;">refresh</i>
            새로운 평가 시작
          </button>
        </div>
      </div>
    </div>

    <!-- Preview/Result Card -->
    <div class="card" v-if="hasData && !stage1Result">
      <div class="card-header bg-gradient-secondary">
        <h5 class="mb-0 text-white">
          <i class="material-icons me-2" style="vertical-align: middle; font-size: 20px;">preview</i>
          데이터 미리보기 (Data Preview)
        </h5>
      </div>
      <div class="card-body">
        <pre class="preview-json">{{ formattedData }}</pre>
        <div class="mt-3 d-flex gap-2">
          <button
            @click="copyToClipboard"
            class="btn btn-primary"
          >
            <i class="material-icons me-2" style="vertical-align: middle;">content_copy</i>
            클립보드에 복사
          </button>
          <button
            @click="downloadJSON"
            class="btn btn-secondary"
          >
            <i class="material-icons me-2" style="vertical-align: middle;">download</i>
            JSON 다운로드
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import APageHead from '@/components/Pineapple/APageHead.vue';
import ABtn from '@/components/Pineapple/ABtn.vue';

export default {
  name: "AIWritingTest",
  components: { APageHead, ABtn },
  data() {
    return {
      // 교재 설정
      config: {
        barcode: '',
        chapterNumber: 1,
        uniqueKey: '',
        targetLanguage: 'ko',  // 타겟 언어 (학습 대상)
        nativeLanguage: 'en'   // 모국어
      },

      // Passage 스크립트
      essayScript: {
        targetEssay: '',  // 타겟 언어 Passage (예: koEssay)
        nativeEssay: ''   // 모국어 Passage (예: enEssay)
      },

      // 질문과 답변
      questionAndAnswer: {
        questions: [],
        answers: []
      },

      // 상태 관리
      isSaving: false,
      isEvaluatingStage1: false,
      isEvaluatingStage3: false,

      // Stage 1 결과
      stage1Result: null,

      // Stage 3 스크립트
      stage3Script: {
        firstScript: '',
        secondScript: ''
      },

      // Stage 3 결과
      stage3Result: null,

      // 사용자 식별자 (실제 환경에서는 Vuex store에서 가져와야 함)
      userIdx: 1,

      // 언어 맵핑
      languageMap: {
        ko: '한국어',
        en: '영어',
        ja: '일본어',
        zh: '중국어',
        es: '스페인어'
      }
    };
  },

  computed: {
    // 데이터가 있는지 확인
    hasData() {
      return this.essayScript.targetEssay ||
             this.essayScript.nativeEssay ||
             this.questionAndAnswer.questions.length > 0;
    },

    // 최종 데이터 구조 생성
    finalData() {
      // 언어별 키 이름 매핑 (기존 구조 유지)
      const essayKeyMap = {
        ko: 'koEssay',
        en: 'enEssay',
        ja: 'jaEssay',
        zh: 'zhEssay',
        es: 'esEssay'
      };

      return {
        config: {
          barcode: this.config.barcode,
          chapterNumber: this.config.chapterNumber,
          uniqueKey: this.config.uniqueKey,
          targetLanguage: this.config.targetLanguage,
          nativeLanguage: this.config.nativeLanguage
        },
        essayScript: {
          [essayKeyMap[this.config.targetLanguage]]: this.essayScript.targetEssay,
          [essayKeyMap[this.config.nativeLanguage]]: this.essayScript.nativeEssay
        },
        questionAndAnswer: {
          questions: this.questionAndAnswer.questions,
          answers: this.questionAndAnswer.answers
        }
      };
    },

    // 미리보기용 포맷된 데이터
    formattedData() {
      return JSON.stringify(this.finalData, null, 2);
    }
  },

  mounted() {
    // 샘플 데이터로 초기화 (개발 편의용)
    this.loadSampleData();
  },

  methods: {
    // 언어 이름 가져오기
    getLanguageName(code) {
      return this.languageMap[code] || code;
    },

    // Q&A 쌍 추가
    addQAPair() {
      this.questionAndAnswer.questions.push('');
      this.questionAndAnswer.answers.push('');
    },

    // 여러 Q&A 한번에 추가
    addMultipleQAPairs() {
      const count = prompt('몇 개의 Q&A를 추가하시겠습니까?', '5');
      if (count && !isNaN(count) && count > 0) {
        for (let i = 0; i < parseInt(count); i++) {
          this.addQAPair();
        }
      }
    },

    // Q&A 쌍 제거
    removeQAPair(index) {
      if (confirm(`Q&A #${index + 1}을 삭제하시겠습니까?`)) {
        this.questionAndAnswer.questions.splice(index, 1);
        this.questionAndAnswer.answers.splice(index, 1);
      }
    },

    // 폼 초기화
    resetForm() {
      if (confirm('모든 입력 내용을 초기화하시겠습니까?')) {
        this.config = {
          barcode: '',
          chapterNumber: 1,
          uniqueKey: '',
          targetLanguage: 'ko',
          nativeLanguage: 'en'
        };
        this.essayScript = {
          targetEssay: '',
          nativeEssay: ''
        };
        this.questionAndAnswer = {
          questions: [],
          answers: []
        };
      }
    },

    // 데이터 저장 (API 호출)
    async saveData() {
      // 필수 필드 검증
      if (!this.config.barcode || !this.config.uniqueKey) {
        alert('교재 바코드와 고유키는 필수 입력 항목입니다.');
        return;
      }

      if (!this.essayScript.targetEssay || !this.essayScript.nativeEssay) {
        alert('타겟 언어와 모국어 Passage는 필수 입력 항목입니다.');
        return;
      }

      this.isSaving = true;

      try {
        // 실제 API 호출 시 아래 코드 사용
        /*
        const response = await this.$axios({
          method: 'post',
          url: '/api/writing-test/save',
          data: this.finalData,
          loadingBar: true
        });

        if (response.data.success) {
          alert('저장되었습니다!');
        } else {
          alert('저장 중 오류가 발생했습니다.');
        }
        */

        // 임시 - 콘솔에 출력
        console.log('저장할 데이터:', this.finalData);
        alert('데이터가 콘솔에 출력되었습니다. (API 연동 전)');
      } catch (error) {
        console.error('저장 오류:', error);
        alert('저장 중 오류가 발생했습니다.');
      } finally {
        this.isSaving = false;
      }
    },

    // 클립보드에 복사
    copyToClipboard() {
      navigator.clipboard.writeText(this.formattedData).then(() => {
        alert('클립보드에 복사되었습니다!');
      }).catch(err => {
        console.error('복사 실패:', err);
        alert('복사에 실패했습니다.');
      });
    },

    // JSON 파일 다운로드
    downloadJSON() {
      const blob = new Blob([this.formattedData], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `writing_test_${this.config.barcode}_ch${this.config.chapterNumber}_${Date.now()}.json`;
      a.click();
      window.URL.revokeObjectURL(url);
    },

    // 샘플 데이터 로드 (개발용)
    loadSampleData() {
      // 예시 데이터 자동 입력
      this.config.barcode = '9781635190151';
      this.config.uniqueKey = '';

      this.essayScript.targetEssay = '이 친구는 제시카이에요. 한국영화클럽 멤버이에요. 미국 사람이에요. 한국 대학교에 다녀요. 일학년이에요.';
      this.essayScript.nativeEssay = 'This friend is Jessica. She is a member of the Korean Film Club. She is American. She goes to Korea University. She is a first-year student.';

      // 샘플 Q&A 추가
      this.questionAndAnswer.questions = [
        '제시카는 어느 대학교에 다녀요?',
        '제시카는 한국영화클럽 멤버예요?',
        '',
        '피터는 어느 대학교에 다녀요?',
        '존는 뉴욕 대학교 학생이에요?',
        '존는 몇 학년이에요?'
      ];

      this.questionAndAnswer.answers = [
        '한국 대학교에 다녀요.',
        '네, 한국영화클럽 멤버예요.',
        '',
        '뉴욕 대학교에 다녀요.',
        '',
        '삼 학년이에요.'
      ];
    },

    // Stage 1 평가 API 호출
    async evaluateStage1() {
      // 필수 필드 검증
      if (!this.config.barcode || !this.config.uniqueKey) {
        alert('교재 바코드와 고유키는 필수 입력 항목입니다.');
        return;
      }

      if (!this.essayScript.targetEssay || !this.essayScript.nativeEssay) {
        alert('타겟 언어와 모국어 Passage는 필수 입력 항목입니다.');
        return;
      }

      this.isEvaluatingStage1 = true;

      try {
        // 쿼리 파라미터 준비
        const params = new URLSearchParams({
          targetLangCode: this.config.targetLanguage,
          firstLanguageCode: this.config.nativeLanguage,
          targetLangEssay: this.essayScript.targetEssay,
          firstLanguageEssay: this.essayScript.nativeEssay,
          questions: JSON.stringify(this.questionAndAnswer.questions),
          answers: JSON.stringify(this.questionAndAnswer.answers)
        });

        // Stage1 API 호출 (최종 평가 상태 조회)
        const response = await this.$axios({
          method: 'post',
          url: `/ai-prompt/evaluation-essay-stage1/${this.userIdx}/${this.config.barcode}/${this.config.chapterNumber}/${this.config.uniqueKey}?${params.toString()}`,
          loadingBar: true
        });

        console.log('Stage1 평가 응답:', response.data);

        // 실제 응답 구조: 최상위 레벨에 필드들이 있음
        if (response.data && response.data.success) {
          // AI 수정 스크립트 추출 (오타 포함 - aiReviedScript)
          // 백엔드가 ai_essay, ai_question_list, ai_answer_list 형식으로 반환
          const aiRevisedScript = response.data.aiReviedScript || {};

          // Stage 1 결과 저장
          this.stage1Result = {
            evalResults: response.data.evalResults,
            overallReview: response.data.overallReview,
            aiRevisedScript: {
              essayScript: aiRevisedScript.ai_essay || '',
              questions: aiRevisedScript.ai_question_list || [],
              answers: aiRevisedScript.ai_answer_list || []
            }
          };

          console.log('저장된 Stage1 결과:', this.stage1Result);
          alert('Stage 1 평가가 완료되었습니다!');
        } else {
          alert('Stage 1 평가 응답 데이터 형식이 올바르지 않습니다.');
        }
      } catch (error) {
        console.error('Stage1 평가 오류:', error);

        let errorMessage = 'Stage 1 평가 중 오류가 발생했습니다.';
        if (error.response) {
          if (error.response.status === 400) {
            errorMessage = '입력 데이터가 올바르지 않습니다.';
          } else if (error.response.status === 404) {
            errorMessage = '교과서 콘텐츠를 찾을 수 없습니다.';
          } else if (error.response.status === 502) {
            errorMessage = 'AI 평가 서비스에 연결할 수 없습니다.';
          } else if (error.response.status === 504) {
            errorMessage = '평가 시간이 초과되었습니다. 다시 시도해주세요.';
          }
        }

        alert(errorMessage);
      } finally {
        this.isEvaluatingStage1 = false;
      }
    },

    // Stage 3 평가 API 호출
    async evaluateStage3() {
      // 필수 필드 검증
      if (!this.stage3Script.firstScript.trim() || !this.stage3Script.secondScript.trim()) {
        alert('First Script와 Second Script를 모두 작성해주세요.');
        return;
      }

      if (!this.stage1Result || !this.stage1Result.aiRevisedScript) {
        alert('Stage 1 평가 결과가 없습니다. 먼저 Stage 1 평가를 완료해주세요.');
        return;
      }

      this.isEvaluatingStage3 = true;

      try {
        // API 문서에 따른 Stage3 요청 데이터 구성 (간소화된 구조)
        const requestData = {
          userScript: {
            essayScript: this.essayScript.targetEssay,
            questions: this.questionAndAnswer.questions,
            answers: this.questionAndAnswer.answers,
            stage3Script: {
              firstScript: this.stage3Script.firstScript,
              secondScript: this.stage3Script.secondScript
            }
          },
          aiRevisedScript: {
            essayScript: this.stage1Result.aiRevisedScript.essayScript,
            questions: this.stage1Result.aiRevisedScript.questions,
            answers: this.stage1Result.aiRevisedScript.answers
          }
        };

        console.log('Stage3 요청 데이터:', requestData);

        // Stage3 API 호출
        const response = await this.$axios({
          method: 'post',
          url: `/ai-prompt/evaluation-essay-stage3/${this.userIdx}/${this.config.barcode}/${this.config.chapterNumber}/${this.config.uniqueKey}`,
          data: requestData,
          loadingBar: true
        });

        console.log('Stage3 평가 응답:', response.data);

        // API 문서에 따른 응답 구조: 최상위 레벨 필드
        if (response.data && response.data.success) {
          // Stage 3 결과 저장 (최상위 레벨에서 필드 추출)
          this.stage3Result = {
            evalResults: response.data.evalResults,
            overallReview: response.data.overallReview,
            aiRevisedScript: response.data.aiReviedScript  // 오타 포함
          };

          console.log('저장된 Stage3 결과:', this.stage3Result);
          alert('Stage 3 평가가 완료되었습니다! 모든 평가가 종료되었습니다.');
        } else {
          alert('Stage 3 평가 응답 데이터 형식이 올바르지 않습니다.');
        }
      } catch (error) {
        console.error('Stage3 평가 오류:', error);

        let errorMessage = 'Stage 3 평가 중 오류가 발생했습니다.';
        if (error.response) {
          if (error.response.status === 400) {
            errorMessage = '입력 데이터가 올바르지 않습니다. 모든 필드를 확인해주세요.';
          } else if (error.response.status === 404) {
            errorMessage = '교과서 콘텐츠를 찾을 수 없습니다.';
          } else if (error.response.status === 502) {
            errorMessage = 'AI 평가 서비스에 연결할 수 없습니다.';
          } else if (error.response.status === 504) {
            errorMessage = '평가 시간이 초과되었습니다. 다시 시도해주세요.';
          }
        }

        alert(errorMessage);
      } finally {
        this.isEvaluatingStage3 = false;
      }
    },

    // 평가 초기화
    resetEvaluation() {
      if (confirm('모든 평가 결과를 초기화하고 새로 시작하시겠습니까?')) {
        this.stage1Result = null;
        this.stage3Result = null;
        this.stage3Script = {
          firstScript: '',
          secondScript: ''
        };
      }
    },

    // AI 수정 Passage 가져오기 (안전하게)
    getAIEssay() {
      if (!this.stage1Result || !this.stage1Result.aiRevisedScript) {
        return 'AI 수정 Passage를 불러올 수 없습니다.';
      }
      return this.stage1Result.aiRevisedScript.essayScript || '수정된 Passage가 없습니다.';
    },

    // AI 수정 질문 목록 가져오기 (안전하게)
    getAIQuestions() {
      if (!this.stage1Result || !this.stage1Result.aiRevisedScript) {
        return [];
      }
      return this.stage1Result.aiRevisedScript.questions || [];
    },

    // AI 수정 답변 목록 가져오기 (안전하게)
    getAIAnswers() {
      if (!this.stage1Result || !this.stage1Result.aiRevisedScript) {
        return [];
      }
      return this.stage1Result.aiRevisedScript.answers || [];
    }
  }
};
</script>

<style scoped>
/* Container */
.ai-writing-test {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
}

/* Gradient backgrounds */
.bg-gradient-dark {
  background: linear-gradient(135deg, #434343 0%, #000000 100%);
  color: white;
}

.bg-gradient-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.bg-gradient-success {
  background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
}

.bg-gradient-info {
  background: linear-gradient(135deg, #36d1dc 0%, #5b86e5 100%);
}

.bg-gradient-warning {
  background: linear-gradient(135deg, #f7971e 0%, #ffd200 100%);
}

.opacity-8 {
  opacity: 0.8;
}

/* Cards */
.card {
  border: none;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 20px;
}

.card:hover {
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}

/* Form Controls */
.form-control,
.form-select {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 10px 15px;
  font-size: 14px;
  transition: all 0.3s ease;
}

.form-control:focus,
.form-select:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
  outline: none;
}

textarea.form-control {
  resize: vertical;
  min-height: 100px;
}

.form-label {
  color: #344767;
  font-size: 13px;
  margin-bottom: 8px;
  font-weight: 600;
}

/* Q&A Pairs */
.qa-container {
  max-height: 600px;
  overflow-y: auto;
  padding: 10px;
}

.qa-pair {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 10px;
  border-left: 4px solid #667eea;
  transition: all 0.3s ease;
}

.qa-pair:hover {
  background: #e9ecef;
  transform: translateX(5px);
}

.qa-header {
  padding-bottom: 10px;
  border-bottom: 1px solid #dee2e6;
}

/* Buttons */
.btn-white {
  background: white;
  color: #344767;
  border: none;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.btn-white:hover {
  background: #f8f9fa;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.btn-success {
  background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
  border: none;
}

.btn-success:hover:not(:disabled) {
  background: linear-gradient(135deg, #38ef7d 0%, #11998e 100%);
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
}

.btn-outline-primary {
  color: #667eea;
  border: 2px solid #667eea;
  background: transparent;
}

.btn-outline-primary:hover {
  background: #667eea;
  color: white;
}

.btn-outline-danger {
  color: #dc3545;
  border: 1px solid #dc3545;
  background: transparent;
  padding: 4px 8px;
}

.btn-outline-danger:hover {
  background: #dc3545;
  color: white;
}

/* Preview */
.preview-json {
  background: #2d2d2d;
  color: #50fa7b;
  padding: 20px;
  border-radius: 8px;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.6;
  max-height: 500px;
  overflow-y: auto;
  margin: 0;
}

/* Alerts */
.alert {
  border: none;
  border-radius: 8px;
  padding: 12px 16px;
}

.alert-info {
  background: linear-gradient(135deg, rgba(54, 209, 220, 0.1) 0%, rgba(91, 134, 229, 0.1) 100%);
  color: #0c5460;
  border-left: 4px solid #36d1dc;
}

.alert-warning {
  background: linear-gradient(135deg, rgba(247, 151, 30, 0.1) 0%, rgba(255, 210, 0, 0.1) 100%);
  color: #856404;
  border-left: 4px solid #f7971e;
}

/* Badges */
.badge {
  padding: 6px 12px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 12px;
}

/* Scrollbar */
.qa-container::-webkit-scrollbar,
.preview-json::-webkit-scrollbar {
  width: 8px;
}

.qa-container::-webkit-scrollbar-track,
.preview-json::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.qa-container::-webkit-scrollbar-thumb,
.preview-json::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 4px;
}

.qa-container::-webkit-scrollbar-thumb:hover,
.preview-json::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
}

/* Small text */
small.text-muted {
  font-size: 12px;
  color: #6c757d !important;
}

/* Additional Gradient Backgrounds */
.bg-gradient-danger {
  background: linear-gradient(135deg, #eb3941 0%, #f15e64 100%);
}

.bg-gradient-secondary {
  background: linear-gradient(135deg, #6c757d 0%, #495057 100%);
}

/* Revised Content Display */
.revised-content {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  border-left: 4px solid #28a745;
}

.revised-text {
  background: transparent;
  border: none;
  margin: 0;
  padding: 0;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-wrap: break-word;
  color: #344767;
}

/* Evaluation Results */
.eval-results .card-header {
  background: #e9ecef;
  font-weight: 600;
  padding: 10px 15px;
}

.eval-results .score-item,
.eval-results .mb-2 {
  padding: 8px 0;
  border-bottom: 1px solid #e9ecef;
}

.eval-results .score-item:last-child,
.eval-results .mb-2:last-child {
  border-bottom: none;
}

/* Overall Review */
.overall-review ul {
  padding-left: 20px;
}

.overall-review li {
  margin-bottom: 5px;
}

/* Table Styling */
.table-bordered {
  border: 2px solid #dee2e6;
}

.table-bordered th {
  background: #f8f9fa;
  font-weight: 600;
  color: #344767;
}

.table-bordered td {
  vertical-align: middle;
}

/* Stage Buttons */
.btn-danger {
  background: linear-gradient(135deg, #eb3941 0%, #f15e64 100%);
  border: none;
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background: linear-gradient(135deg, #f15e64 0%, #eb3941 100%);
}

.btn-danger:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-warning {
  background: linear-gradient(135deg, #f7971e 0%, #ffd200 100%);
  border: none;
  color: #333;
}

.btn-warning:hover:not(:disabled) {
  background: linear-gradient(135deg, #ffd200 0%, #f7971e 100%);
}

.btn-warning:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Evaluation Progress */
.evaluation-progress {
  padding: 20px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 10px;
  border: 2px solid #dee2e6;
}

.progress-header h6 {
  color: #344767;
  margin-bottom: 5px;
}

.rotating {
  animation: rotate 2s linear infinite;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.spinner-border {
  width: 3rem;
  height: 3rem;
  border-width: 0.3em;
}

/* Responsive */
@media (max-width: 768px) {
  .ai-writing-test {
    padding: 10px;
  }

  .qa-pair {
    padding: 10px;
  }

  .preview-json {
    font-size: 11px;
    padding: 15px;
  }

  .table-responsive {
    font-size: 13px;
  }

  .revised-content {
    padding: 10px;
  }

  .btn-lg {
    font-size: 14px;
    padding: 10px 20px;
  }

  .evaluation-progress {
    padding: 15px;
  }
}
</style>